'use strict';

const NORMAL = 'normal';
const WARNING = 'warning';
const BLOCKING = 'blocking';
const UNCONFIGURED = 'unconfigured';

function validateObservationValue(range, value) {
  if (!isValidRange(range)) {
    return result(UNCONFIGURED, 'invalid_configuration', value);
  }

  const numericValue = parseNumericValue(value);
  if (numericValue === undefined) {
    return result(BLOCKING, 'invalid_format', value);
  }
  if (numericValue < range.min) {
    return result(BLOCKING, 'invalid_low', value);
  }
  if (numericValue > range.max) {
    return result(BLOCKING, 'invalid_high', value);
  }
  if (range.warningMin !== undefined && numericValue <= range.warningMin) {
    return result(WARNING, 'warning_low', value);
  }
  if (range.warningMax !== undefined && numericValue >= range.warningMax) {
    return result(WARNING, 'warning_high', value);
  }
  return result(NORMAL, NORMAL, value);
}

function validateBloodPressure(range, value) {
  if (!isValidBloodPressureRange(range)) {
    return result(UNCONFIGURED, 'invalid_configuration', value);
  }

  const parsed = parseBloodPressure(value);
  if (!parsed) {
    return result(BLOCKING, 'invalid_format', value);
  }

  const systolic = validateObservationValue(range.systolic, parsed.systolic);
  const diastolic = validateObservationValue(range.diastolic, parsed.diastolic);
  const parts = { systolic, diastolic };
  const invalidPart = [systolic, diastolic].find(part => part.severity === BLOCKING);
  if (invalidPart) {
    return { ...result(BLOCKING, invalidPart.code, value), parts };
  }
  if (parsed.diastolic >= parsed.systolic) {
    return { ...result(BLOCKING, 'invalid_relationship', value), parts };
  }

  const warningPart = [systolic, diastolic].find(part => part.severity === WARNING);
  if (warningPart) {
    return { ...result(WARNING, warningPart.code, value), parts };
  }
  return { ...result(NORMAL, NORMAL, value), parts };
}

function createObservationWarningFingerprint(context, value) {
  return JSON.stringify([canonicalize(context), canonicalize(value)]);
}

function result(severity, code, value) {
  return { severity, code, value };
}

function isValidBloodPressureRange(range) {
  return isPlainObject(range)
    && range.unit === 'mmHg'
    && isValidRange(range.systolic)
    && isValidRange(range.diastolic);
}

function isValidRange(range) {
  if (!isPlainObject(range)
    || !Number.isFinite(range.min)
    || !Number.isFinite(range.max)
    || range.min > range.max) return false;

  if (range.warningMin !== undefined
    && (!Number.isFinite(range.warningMin)
      || range.warningMin < range.min
      || range.warningMin > range.max)) return false;
  if (range.warningMax !== undefined
    && (!Number.isFinite(range.warningMax)
      || range.warningMax < range.min
      || range.warningMax > range.max)) return false;
  return range.warningMin === undefined
    || range.warningMax === undefined
    || range.warningMin < range.warningMax;
}

function parseNumericValue(value) {
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
  if (typeof value !== 'string' || value.trim() === '') return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function parseBloodPressure(value) {
  if (typeof value !== 'string') return undefined;
  const match = value.match(/^\s*(\d+)\s*\/\s*(\d+)\s*$/);
  if (!match) return undefined;
  return { systolic: Number(match[1]), diastolic: Number(match[2]) };
}

function canonicalize(value) {
  if (Array.isArray(value)) return value.map(canonicalize);
  if (isPlainObject(value)) {
    return Object.fromEntries(Object.keys(value).sort().map(key => [key, canonicalize(value[key])]));
  }
  if (typeof value === 'number' && !Number.isFinite(value)) return String(value);
  if (value === undefined) return '__undefined__';
  return value;
}

function isPlainObject(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

module.exports = {
  createObservationWarningFingerprint,
  validateBloodPressure,
  validateObservationValue,
};
